import { ActivityStreamHelper } from '../activity-stream-helper';
import { ActivityStreamEventOperation } from '../models/activity-stream-event-operation.enum';
import { ActivityStreamItemEventType } from '../models/activity-stream-event-type.enum';
import { ActivityStreamItemType } from '../models/activity-stream-item-type.enum';
import { ActivityStreamItem } from '../models/activity-stream-item.model';
import { ActivityStreamOrganisationManagementEventType } from '../models/activity-stream-organisation-mangement-event-type.enum';
import { ActivityStreamPropertyItemTypeMapping } from './activity-stream-evaluation-member-item-type.config';

export class ActivityStreamEventTypeMapping {
  public static getActivityStreamItemType(item: ActivityStreamItem): ActivityStreamItemType {
    switch (item.eventType) {
      case ActivityStreamItemEventType.JobAssignmentProposal:
        return ActivityStreamItemType.ShortlistChanged;

      case ActivityStreamItemEventType.SpecialPaymentApproval:
        return item.changedProperties?.length === 1 && item.changedProperties[0].propertyName === 'ExpectedApproverId'
          ? ActivityStreamItemType.ApproverChanged
          : ActivityStreamItemType.StatusChanged;

      case ActivityStreamItemEventType.PositionRequest:
        return ActivityStreamItemType.InquiryApprovalProposal;

      case ActivityStreamItemEventType.PositionRequestApproval:
        return ActivityStreamItemType.InquiryApprovalStep;

      case ActivityStreamItemEventType.EmployeeSnapshotManagerChanged:
        return ActivityStreamItemType.ManagerChanged;

      case ActivityStreamItemEventType.PositionRecord:
        switch (item.eventOperation) {
          case ActivityStreamEventOperation.Created:
            return ActivityStreamItemType.CreatedPosition;
          case ActivityStreamEventOperation.Deleted:
            return ActivityStreamItemType.DeletedPosition;
          case ActivityStreamEventOperation.Updated:
            if (ActivityStreamHelper.hasSomePropertyChanged(item, 'OrganisationChartId')) {
              return ActivityStreamItemType.MovedPositionBetweenOrgCharts;
            }
            if (ActivityStreamHelper.hasSomePropertyChanged(item, 'Title', 'OrgCode')) {
              return ActivityStreamItemType.MovedPosition;
            }
            if (
              item.changedProperties?.length === 1 &&
              item.changedProperties?.some(property => property.propertyName === 'IsHead')
            ) {
              if (item.changedProperties[0].currentValue === 'True') {
                return ActivityStreamItemType.SetAsHeadPosition;
              }
              return ActivityStreamItemType.RemovedAsHeadPosition;
            }
        }

        break;

      case ActivityStreamItemEventType.EmployeeRecord:
        if (ActivityStreamHelper.hasSomePropertyChanged(item, 'OrganisationChartId')) {
          return ActivityStreamItemType.MovedEmployeeBetweenOrgCharts;
        }

        if (ActivityStreamHelper.hasSomePropertyChanged(item, 'OrganisationChartId')) {
          return ActivityStreamItemType.MovedEmployeeBetweenOrgCharts;
        }

        break;

      case ActivityStreamItemEventType.JobAssignmentRecordPositionRecord:
        if (ActivityStreamHelper.hasSomePropertyChanged(item, 'EmployeeId')) {
          const employeeIdProperty = ActivityStreamHelper.getProperty(item, 'EmployeeId');
          // Current BE behaviour, when both current and original values are set it is considered an unassignment
          if (employeeIdProperty.currentValue && employeeIdProperty.originalValue) {
            return ActivityStreamItemType.UnassignedEmployee;
          }
          if (!employeeIdProperty.originalValue) {
            return ActivityStreamItemType.AssignedEmployee;
          }
        }

        break;

      case ActivityStreamItemEventType.JobAssignmentRecordEmployeeRecord:
        if (ActivityStreamHelper.hasSomePropertyChanged(item, 'EmployeeId')) {
          if (item.eventOperation === ActivityStreamEventOperation.Created) {
            return ActivityStreamItemType.AssignedPosition;
          }
          if (item.eventOperation === ActivityStreamEventOperation.Deleted) {
            return ActivityStreamItemType.UnassignedPosition;
          }
        }

        break;

      case ActivityStreamItemEventType.OrganisationRecord:
        if (item.changedProperties.every(property => !property.originalValue)) {
          return ActivityStreamItemType.CreatedOrganisation;
        }
        if (ActivityStreamHelper.hasSomePropertyChanged(item, 'OrganisationChartId')) {
          return ActivityStreamItemType.MovedOrganisationBetweenOrgCharts;
        }
        if (item.changedProperties.some(property => property.propertyName === 'ParentOrgCode')) {
          return ActivityStreamItemType.MovedOrganisation;
        }
        if (
          item.changedProperties.some(property => ['OrgCode', 'OrgName', 'OrgType'].includes(property.propertyName))
        ) {
          return ActivityStreamItemType.ChangedInformation;
        }

        break;

      case ActivityStreamItemEventType.PositionOrganisationAssignmentChanged:
        return ActivityStreamItemType.OrganisationChanged;

      case ActivityStreamItemEventType.AutoApproveAllowedChanged:
      case ActivityStreamItemEventType.ApprovalRequiredChanged:
      case ActivityStreamItemEventType.PayrollStateReset:
      case ActivityStreamItemEventType.PayrollExported:
      case ActivityStreamItemEventType.SeasonCompensationOverlaid:
      case ActivityStreamItemEventType.SeasonBenchmarkUpdateTriggered:
      case ActivityStreamItemEventType.EmployeeSnapshotDataUpdated:
      case ActivityStreamItemEventType.MercerDataTransferred:
        return item.eventType as unknown as ActivityStreamItemType;
      case ActivityStreamItemEventType.ReassignmentComparisonExecuted:
        return ActivityStreamItemType.ReassignmentComparisonExecuted;

      case ActivityStreamItemEventType.Record:
        if (ActivityStreamHelper.getProperty(item, 'PayrollState')?.currentValue === 'Done') {
          return ActivityStreamItemType.PayrollExported;
        }
        if (ActivityStreamHelper.getProperty(item, 'PayrollState')?.currentValue === 'Open') {
          return ActivityStreamItemType.PayrollStateReset;
        }

        break;

      case ActivityStreamItemEventType.TalentRecommendation:
        switch (item.eventOperation) {
          case ActivityStreamEventOperation.Created:
            return ActivityStreamItemType.NewRecommendationAdded;
          case ActivityStreamEventOperation.Deleted:
            return ActivityStreamItemType.TalentRemovedFromPool;
          case ActivityStreamEventOperation.Updated:
            if (ActivityStreamHelper.hasSomePropertyChanged(item, 'TalentPoolId')) {
              return ActivityStreamItemType.InformationChanged;
            }

            if (
              this.isRevertedRecommendation(
                ActivityStreamHelper.getProperty(item, 'RecommendationState')?.originalValue as string,
                ActivityStreamHelper.getProperty(item, 'RecommendationState')?.currentValue as string
              )
            ) {
              return ActivityStreamItemType.RevertedRecommendation;
            }

            return ActivityStreamItemType.StateChanged;
        }

        break;

      case ActivityStreamItemEventType.TalentPoolAssignment:
        if (item.eventOperation === ActivityStreamEventOperation.Created) {
          if (!ActivityStreamHelper.getProperty(item, 'RecommendationState')) {
            return ActivityStreamItemType.NewTalentAddedToPool;
          }
          return ActivityStreamItemType.NewRecommendationAdded;
        }
        if (item.eventOperation === ActivityStreamEventOperation.Deleted) {
          return ActivityStreamItemType.TalentRemovedFromPool;
        }

        break;

      case ActivityStreamItemEventType.StandingPositionEvaluationRequest:
        return ActivityStreamItemType.EvaluationRequest;

      case ActivityStreamItemEventType.StandingPositionEvaluationRequestApproval:
        return ActivityStreamItemType.MasterDataTransfer;

      case ActivityStreamItemEventType.StandingPositionEvaluationMasterDataJobCode:
        return ActivityStreamItemType.MasterDataJobCode;

      case ActivityStreamItemEventType.StandingPositionEvaluationMasterDataPositionClass:
        return ActivityStreamItemType.MasterDataPositionClass;

      case ActivityStreamItemEventType.StandingPositionEvaluationMasterDataEmployee:
        return ActivityStreamItemType.MasterDataEmployee;

      // Exceptional Recognitions
      case ActivityStreamItemEventType.ExceptionalRecognitionsProposal:
        return ActivityStreamItemType.InformationChanged;
      case ActivityStreamItemEventType.ExceptionalRecognitionsApproval:
        return item.changedProperties?.length === 1 && item.changedProperties[0].propertyName === 'ExpectedApproverId'
          ? ActivityStreamItemType.ApproverChanged
          : ActivityStreamItemType.StatusChanged;
      case ActivityStreamItemEventType.ExceptionalRecognitionsBoardFlag:
        return ActivityStreamItemType.StatusChanged;
      case ActivityStreamItemEventType.ExceptionalRecognitionsCfoFlag:
        return ActivityStreamItemType.StatusChanged;
      case ActivityStreamItemEventType.ExceptionalRecognitionsPayrollExport:
        return ActivityStreamItemType.PayrollExported;
      case ActivityStreamItemEventType.ExceptionalRecognitionsEmployeeMapping:
        return ActivityStreamItemType.InformationChanged;

      case ActivityStreamItemEventType.OrganisationManagement:
        switch (
          ActivityStreamHelper.getProperty(item, 'EventType')?.currentValue ??
          ActivityStreamHelper.getProperty(item, 'EventType')?.originalValue
        ) {
          case ActivityStreamOrganisationManagementEventType.Position:
            switch (item.eventOperation) {
              case ActivityStreamEventOperation.Created:
                return ActivityStreamItemType.CreatedPosition;
              case ActivityStreamEventOperation.Updated:
                return ActivityStreamItemType.UpdatedPosition;
              case ActivityStreamEventOperation.Deleted:
                return ActivityStreamItemType.DeletedPosition;
            }

            break;

          case ActivityStreamOrganisationManagementEventType.OrganisationRecord:
            switch (item.eventOperation) {
              case ActivityStreamEventOperation.Created:
                return ActivityStreamItemType.CreatedOrganisationRecord;
              case ActivityStreamEventOperation.Updated:
                return ActivityStreamItemType.UpdatedOrganisationRecord;
              case ActivityStreamEventOperation.Deleted:
                return ActivityStreamItemType.DeletedOrganisationRecord;
            }

            break;

          case ActivityStreamOrganisationManagementEventType.Role:
            switch (item.eventOperation) {
              case ActivityStreamEventOperation.Created:
                return ActivityStreamItemType.CreatedEmployeeRole;
              case ActivityStreamEventOperation.Updated:
                return ActivityStreamItemType.UpdatedEmployeeRole;
              case ActivityStreamEventOperation.Deleted:
                return ActivityStreamItemType.DeletedEmployeeRole;
            }

            break;

          case ActivityStreamOrganisationManagementEventType.EmployeeLineManagerRecord:
            return ActivityStreamItemType.EmployeeLineManagerUpdated;

          default:
            switch (item.eventOperation) {
              case ActivityStreamEventOperation.Created:
                return ActivityStreamItemType.CreatedOrganisation;
              case ActivityStreamEventOperation.Updated:
                return ActivityStreamItemType.UpdatedOrganisation;
              case ActivityStreamEventOperation.Deleted:
                return ActivityStreamItemType.DeletedOrganisation;
            }
        }

        break;

      case ActivityStreamItemEventType.Template:
        switch (item.eventOperation) {
          case ActivityStreamEventOperation.Created:
            return ActivityStreamItemType.CreatedTemplate;
          case ActivityStreamEventOperation.Updated:
            return ActivityStreamItemType.UpdatedTemplate;
          default:
            break;
        }

        break;

      case ActivityStreamItemEventType.TemplateVersion:
        switch (item.eventOperation) {
          case ActivityStreamEventOperation.Created:
            return ActivityStreamItemType.CreatedTemplateVersion;
          case ActivityStreamEventOperation.Updated:
            if (item.changedProperties?.findIndex(property => property.propertyName === 'Status') >= 0) {
              switch (item.changedProperties.find(property => property.propertyName === 'Status').currentValue) {
                case 'PendingApproval':
                  return ActivityStreamItemType.TemplateVersionSentToApprover;
                case 'Published':
                  return ActivityStreamItemType.TemplateVersionApproved;
                case 'Draft':
                  return item.changedProperties?.findIndex(property => property.propertyName === 'Comment') >= 0
                    ? ActivityStreamItemType.TemplateVersionRejected
                    : ActivityStreamItemType.ApprovalWorkflowAborted;
                case 'Retired':
                  return ActivityStreamItemType.TemplateVersionRetired;
                default:
                  break;
              }
            }

            return ActivityStreamItemType.UpdatedTemplateVersion;
          default:
            break;
        }

        break;

      case ActivityStreamItemEventType.TemplateVersionContent:
        return ActivityStreamItemType.UpdatedTemplateVersionContent;

      case ActivityStreamItemEventType.TemplateVersionSettings:
        return ActivityStreamItemType.UpdatedTemplateVersionSettings;

      case ActivityStreamItemEventType.TemplateVersionFooter:
        return ActivityStreamItemType.UpdatedTemplateVersionFooter;

      case ActivityStreamItemEventType.TemplateVersionFooterTranslation:
        return ActivityStreamItemType.UpdatedTemplateVersionFooterTranslation;

      case ActivityStreamItemEventType.TemplateVersionHeader:
        return ActivityStreamItemType.UpdatedTemplateVersionHeader;

      case ActivityStreamItemEventType.TemplateVersionHeaderTranslation:
        return ActivityStreamItemType.UpdatedTemplateVersionHeaderTranslation;

      case ActivityStreamItemEventType.TemplateVersionAcknowledgement:
        return ActivityStreamItemType.UpdatedTemplateVersionAcknowledgement;

      case ActivityStreamItemEventType.TemplateVersionAcknowledgementTranslation:
        return ActivityStreamItemType.UpdatedTemplateVersionAcknowledgementTranslation;

      case ActivityStreamItemEventType.Clause:
        switch (item.eventOperation) {
          case ActivityStreamEventOperation.Created:
            return ActivityStreamItemType.CreatedClause;
          case ActivityStreamEventOperation.Updated:
            return ActivityStreamItemType.UpdatedClause;
          case ActivityStreamEventOperation.Deleted:
            return ActivityStreamItemType.DeletedClause;
          default:
            break;
        }

        break;

      case ActivityStreamItemEventType.ClauseVersion:
        switch (item.eventOperation) {
          case ActivityStreamEventOperation.Created:
            return ActivityStreamItemType.CreatedClauseVersion;
          case ActivityStreamEventOperation.Updated:
            if (item.changedProperties?.findIndex(property => property.propertyName === 'ClauseStatus') >= 0) {
              switch (item.changedProperties.find(property => property.propertyName === 'ClauseStatus').currentValue) {
                case 'PendingApproval':
                  return ActivityStreamItemType.ClauseVersionSentToApprover;
                case 'Published':
                  return ActivityStreamItemType.ClauseVersionApproved;
                case 'Draft':
                  return item.changedProperties?.findIndex(property => property.propertyName === 'Comment') >= 0
                    ? ActivityStreamItemType.ClauseVersionRejected
                    : ActivityStreamItemType.ApprovalWorkflowAborted;
                case 'Retired':
                  return ActivityStreamItemType.ClauseVersionRetired;
                default:
                  break;
              }
            }

            return ActivityStreamItemType.UpdatedClauseVersion;
          case ActivityStreamEventOperation.Deleted:
            return ActivityStreamItemType.DeletedClauseVersion;
          default:
            break;
        }

        break;

      case ActivityStreamItemEventType.ClauseTranslation:
        switch (item.eventOperation) {
          case ActivityStreamEventOperation.Created:
            return ActivityStreamItemType.CreatedClauseTranslation;
          case ActivityStreamEventOperation.Updated:
            return ActivityStreamItemType.UpdatedClauseTranslation;
          case ActivityStreamEventOperation.Deleted:
            return ActivityStreamItemType.DeletedClauseTranslation;
          default:
            break;
        }

        break;

      default:
        break;
    }

    if (
      item.changedProperties?.some(property =>
        ['ManuallyChanged', 'NewHire', 'Promotion', 'Termination', 'PopulationChange'].includes(property.propertyName)
      )
    ) {
      return ActivityStreamItemType.StatusChanged;
    }

    if (
      item.changedProperties?.some(property =>
        ['MeritSettingsChanged', 'EquitySettingsChanged', 'EquitySubSettingsChanged'].includes(property.propertyName)
      )
    ) {
      return ActivityStreamItemType.PlanChanged;
    }

    return !item.changedProperties?.length || item.changedProperties?.length > 1
      ? ActivityStreamItemType.InformationChanged
      : (ActivityStreamPropertyItemTypeMapping[item.changedProperties[0].propertyName] ??
          ActivityStreamItemType.InformationChanged);
  }

  private static isRevertedRecommendation(originalValue: string, currentValue: string): boolean {
    const stateValues = ['Approved', 'Declined', 'ReviewerAccepted', 'ReviewerDeclined', 'Recommended'];
    const originalIndex = stateValues.indexOf(originalValue);
    const currentIndex = stateValues.indexOf(currentValue);
    return originalIndex < currentIndex;
  }
}
